/**
 * Project hooks (§5.2 "Projects": list/create/detail/update/delete, stats, next task).
 * Classes and members live in `classes.ts` / `members.ts`. Owner: frontend-foundation.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type {
  ArchivedFilter,
  NextTask,
  NextTaskParams,
  Project,
  ProjectCreate,
  ProjectStats,
  ProjectSummary,
  ProjectUpdate,
} from '../types';

/** Projects of the caller; `archived`: active (default), only archived, or all. */
export const projectsQuery = (archived: ArchivedFilter = 'false') =>
  queryOptions({
    queryKey: [...qk.projects.list(), archived] as const,
    queryFn: ({ signal }) =>
      api.get<ProjectSummary[]>('/api/projects', { signal, query: archived === 'false' ? {} : { archived } }),
  });

export function useProjects(archived: ArchivedFilter = 'false') {
  return useQuery(projectsQuery(archived));
}

export const projectQuery = (pid: number) =>
  queryOptions({
    queryKey: qk.projects.detail(pid),
    queryFn: ({ signal }) => api.get<Project>(`/api/projects/${pid}`, { signal }),
  });

/** Project detail incl. settings, classes, members and the caller's `role`. */
export function useProject(pid: number) {
  return useQuery(projectQuery(pid));
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectCreate) => api.post<Project>('/api/projects', body),
    onSuccess: (project) => {
      queryClient.setQueryData(qk.projects.detail(project.id), project);
      return queryClient.invalidateQueries({ queryKey: qk.projects.list() });
    },
    meta: { errorToast: false },
  });
}

/** PATCH a project; `settings` is a full replace (validated server-side). */
export function useUpdateProject(pid: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProjectUpdate) => api.patch<Project>(`/api/projects/${pid}`, patch),
    onSuccess: (project) => {
      queryClient.setQueryData(qk.projects.detail(pid), project);
      return queryClient.invalidateQueries({ queryKey: qk.projects.list() });
    },
    meta: { errorToast: false },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pid: number) => api.delete(`/api/projects/${pid}`),
    onSuccess: (_data, pid) => {
      queryClient.removeQueries({ queryKey: qk.projects.scope(pid) });
      return queryClient.invalidateQueries({ queryKey: qk.projects.list() });
    },
    meta: { errorTitle: 'Could not delete the project' },
  });
}

export const projectStatsQuery = (pid: number) =>
  queryOptions({
    queryKey: qk.projects.stats(pid),
    queryFn: ({ signal }) => api.get<ProjectStats>(`/api/projects/${pid}/stats`, { signal }),
  });

export function useProjectStats(pid: number) {
  return useQuery(projectStatsQuery(pid));
}

/**
 * Next image to annotate / review after `after` (`image_id: null` = nothing left).
 * Typically fetched imperatively: `queryClient.fetchQuery(nextTaskQuery(pid, {mode, after}))`.
 */
export const nextTaskQuery = (pid: number, params: NextTaskParams) =>
  queryOptions({
    queryKey: qk.projects.nextTask(pid, params),
    queryFn: ({ signal }) => api.get<NextTask>(`/api/projects/${pid}/next-task`, { signal, query: params }),
    staleTime: 0,
  });
